import React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css, keyframes } from 'styled-components'
import useTranscriptionLogContext from '~/pages/CallAnalytics/Transcription/context/TranscriptionLogContext'
import { formatMilliseconds } from '~/utils/formatter'

const PauseWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 14px 2px;
`

interface PauseProps {
  $active: boolean
}

const inactivePauseBackgroundColor = 'var(--chakra-colors-bg-muted)'
const activeGradientKeyframes = keyframes`
  0% {
    outline-color: var(--chakra-colors-border-default);
    background-color: var(--chakra-colors-bg-muted);
  }
  50% {
    outline-color: var(--chakra-colors-fg-subtle);
    background-color: var(--chakra-colors-border-default);
  }
  100% {
    outline-color: var(--chakra-colors-border-default);
    background-color: var(--chakra-colors-bg-muted);
  }
`
const Pause = styled.span<PauseProps>`
  display: flex;
  flex-direction: row;
  height: 21px;
  background-color: ${(props) =>
    props.$active
      ? 'var(--chakra-colors-bg-muted)'
      : inactivePauseBackgroundColor};
  background-size: ${(props) => (props.$active ? '400% 400%' : '')};
  border-radius: 4px;
  outline: 2px dotted;
  outline-color: var(--chakra-colors-border-default);
  align-items: center;
  justify-content: center;
  ${(props) =>
    props.$active &&
    css`
      animation: ${activeGradientKeyframes} 2.25s linear infinite;
    `}
`

const textAnimation = keyframes`
  0% {
    color: var(--chakra-colors-border-default);
  }
  50% {
    color: var(--chakra-colors-fg-subtle);
  }
  100% {
    color: var(--chakra-colors-fg-muted);
  }
`
const PauseText = styled.span<PauseProps>`
  font-family: 'Inter';
  font-size: 14px;
  color: var(--chakra-colors-fg-muted);
  font-weight: bold;
  ${(props) =>
    props.$active &&
    css`
      animation: ${textAnimation} 2.25s linear infinite;
    `};
`

interface TranscriptLogPauseProps {
  pauseStartTimestampMillis?: number
  pauseEndTimestampMillis?: number
}

const TranscriptLogPause = ({
  pauseStartTimestampMillis,
  pauseEndTimestampMillis,
}: TranscriptLogPauseProps) => {
  const { t } = useTranslation()
  const { isPlaying, elapsedTimeMillis } = useTranscriptionLogContext()

  const pauseDurationMilliseconds =
    (pauseEndTimestampMillis ?? 0) - (pauseStartTimestampMillis ?? 0)
  const renderPause = pauseDurationMilliseconds > 1000 * 15 //15 seconds

  const active =
    isPlaying &&
    renderPause &&
    elapsedTimeMillis > pauseStartTimestampMillis! &&
    elapsedTimeMillis < pauseEndTimestampMillis!

  return renderPause ? (
    <PauseWrapper>
      <Pause $active={active}>
        <PauseText $active={active}>
          {t('callAnalytics.transcript.silence', 'Silence {{duration}}', {
            duration: formatMilliseconds(pauseDurationMilliseconds),
          })}
        </PauseText>
      </Pause>
    </PauseWrapper>
  ) : null
}

export default TranscriptLogPause
